// Per-table column visibility, persisted so a reader who hides four columns to compare
// two others still has that layout after a refresh.
//
// Promoted out of MIS_work_2 (`mfd/useMfdColumns`, which is now a re-export shim) when the
// Leads and Orders tables needed the same control; both modules import it from here.
//
// STORES THE HIDDEN SET, NOT THE VISIBLE ONE. That is the whole reason this is a hook and
// not a `useState<string[]>` at each call site: with the visible list persisted, adding a
// column later means every reader who has ever touched the control never sees the new
// column - their stored list predates it and looks deliberate. Storing what was hidden
// makes "everything else" the default, so a new column shows up for everybody.

import { useCallback, useMemo } from 'react';

import usePersistedState from '@/hooks/usePersistedState';

// Module-level, NOT a `= []` default in the signature: that allocates a fresh
// array on every render of every caller that omits it, which changes the
// identity of `isLocked`, `hiddenSet` and `visibleKeys` every render. The MFD
// tables memoise off `visibleKeys` precisely so a 17x10 table does not re-filter
// on every render (MfdStateTable, MfdCompareAumChart); an unstable default
// silently turns those memos into decoration.
const NO_LOCKED_KEYS: readonly string[] = [];

export interface TableColumnsApi<K extends string> {
  /** Whether a column should be rendered. */
  isVisible: (key: K) => boolean;
  /** Hide a visible column, or show a hidden one. Locked keys never move. */
  toggle: (key: K) => void;
  /** Whether a column is pinned on and cannot be hidden. */
  isLocked: (key: K) => boolean;
  /** Show every column again. */
  reset: () => void;
  /** True when nothing is hidden, so the Reset control can be disabled. */
  isDefault: boolean;
  hiddenCount: number;
  /** Keys still on screen, in the caller's original order. */
  visibleKeys: K[];
}

export const useTableColumns = <K extends string>(
  storageKey: string,
  allKeys: readonly K[],
  // Columns the table is useless without - the identifying ones (ID, ARN, name,
  // email, mobile) on the Leads/Orders tables. They stay visible and their
  // checkbox is disabled; hiding them is not a layout preference but a way to
  // end up with rows nobody can tell apart.
  lockedKeys: readonly K[] = NO_LOCKED_KEYS as readonly K[],
): TableColumnsApi<K> => {
  const [hidden, setHidden] = usePersistedState<K[]>(storageKey, []);

  const isLocked = useCallback((key: K) => lockedKeys.includes(key), [lockedKeys]);

  /*
    Normalised on every read, for the same reason the chart series list is: this comes back
    from sessionStorage via JSON.parse with no shape check, so it can be a non-array (which
    makes `.includes` throw and takes the table down) or can name a column that has since
    been renamed or removed (which would silently hide nothing while the Reset button
    claims there is something to reset). A key that has since become locked is dropped
    here too, so an older stored layout cannot keep an identifying column hidden.
  */
  const hiddenSet = useMemo(() => {
    if (!Array.isArray(hidden)) return new Set<K>();
    return new Set(allKeys.filter((key) => hidden.includes(key) && !lockedKeys.includes(key)));
  }, [hidden, allKeys, lockedKeys]);

  const isVisible = useCallback((key: K) => !hiddenSet.has(key), [hiddenSet]);

  const toggle = useCallback(
    (key: K): void => {
      const next = new Set(hiddenSet);
      if (next.has(key)) {
        next.delete(key);
      } else {
        if (lockedKeys.includes(key)) return;
        // A table with no columns is a blank box with no way back except Reset, which is
        // itself off screen at that point. The last visible column cannot be hidden; the
        // checkbox for it is disabled too, so this is the keyboard/programmatic backstop.
        if (allKeys.length - next.size <= 1) return;
        next.add(key);
      }
      // Written in the caller's key order rather than insertion order, so the stored value
      // is stable and does not depend on which order the columns were clicked in.
      setHidden(allKeys.filter((k) => next.has(k)));
    },
    [hiddenSet, allKeys, lockedKeys, setHidden],
  );

  const reset = useCallback(() => setHidden([]), [setHidden]);

  const visibleKeys = useMemo(
    () => allKeys.filter((key) => !hiddenSet.has(key)),
    [allKeys, hiddenSet],
  );

  return {
    isVisible,
    toggle,
    isLocked,
    reset,
    isDefault: hiddenSet.size === 0,
    hiddenCount: hiddenSet.size,
    visibleKeys,
  };
};
